// The on-screen keyboard for a touch-screen till (2026-10-02).
//
// GIOSHOP's till is a touch screen with no keyboard. Windows' own touch
// keyboard comes up only if a Windows setting is on, types Georgian only if
// Georgian is installed, and is a full QWERTY sheet for typing „5" into a cash
// field. So a device can switch on OUR keyboard (Settings → გარეგნობა, per
// device like the palette and the density — the till wants it, the owner's
// laptop and phone do not): a number pad for amounts, quantities and PINs,
// Georgian letters for searching. components/touch-keyboard.tsx draws it;
// everything here is the arithmetic, kept pure so it is tested in node.

/** localStorage key — per device. */
export const TOUCH_KB_STORAGE_KEY = 'touchKeyboard'
/** Fired on window when the setting changes, so the mounted keyboard follows at once. */
export const TOUCH_KB_EVENT = 'martepos:touchkeyboard'

export function getTouchKeyboard(): boolean {
  try {
    return localStorage.getItem(TOUCH_KB_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function setTouchKeyboard(on: boolean): void {
  try {
    if (on) localStorage.setItem(TOUCH_KB_STORAGE_KEY, '1')
    else localStorage.removeItem(TOUCH_KB_STORAGE_KEY)
  } catch { /* private mode: this session only */ }
  window.dispatchEvent(new Event(TOUCH_KB_EVENT))
}

/** The attribute on the keyboard's own element (components/touch-keyboard.tsx). */
export const TOUCH_KB_HOST = 'data-vkb-host'

/**
 * A press (or a focus move) that came from the on-screen keyboard. A dialog
 * must not read it as „outside" and close — the key is typing into it.
 */
export function fromTouchKeyboard(e: Event | null | undefined): boolean {
  if (!e || typeof Element === 'undefined') return false
  const nodes = [e.target, (e as FocusEvent).relatedTarget]
  return nodes.some((n) => n instanceof Element && n.closest(`[${TOUCH_KB_HOST}]`) !== null)
}

// ── Which keyboard ───────────────────────────────────────────────────────────

export type KbKind = 'numeric' | 'text'

/** What the keyboard needs to know about a field — read off the DOM by the component. */
export type FieldDesc = {
  tag: string
  /** input.type, lowercased; '' for a textarea */
  type: string
  /** the field's OWN inputmode, before the keyboard set it to none */
  inputMode: string
  readOnly: boolean
  disabled: boolean
  /** inside [data-vkb="off"] */
  optOut: boolean
}

const TEXT_TYPES = new Set(['text', 'search', 'email', 'url', 'password'])

export function kindOf(f: FieldDesc): KbKind | null {
  if (f.readOnly || f.disabled || f.optOut) return null
  // inputmode=none: the field draws its own pad (or wants no keyboard at all)
  if (f.inputMode === 'none') return null
  if (f.tag === 'textarea') return 'text'
  if (f.tag !== 'input') return null
  if (f.type === 'number' || f.type === 'tel') return 'numeric'
  if (f.inputMode === 'numeric' || f.inputMode === 'decimal' || f.inputMode === 'tel') return 'numeric'
  return TEXT_TYPES.has(f.type) ? 'text' : null
}

/** Money and weight take a point; a PIN, a phone and a whole-number field do not. */
export function allowsDot(f: { type: string; inputMode: string; step: string }): boolean {
  if (f.inputMode === 'decimal') return true
  if (f.type !== 'number') return false
  if (f.step === 'any') return true
  // no step = the default step 1: whole numbers
  return f.step !== '' && !Number.isInteger(Number(f.step))
}

// ── The number pad ───────────────────────────────────────────────────────────

export type NumKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.' | 'back' | 'clear'

/**
 * The pad's own reading of the field. `draft` null = a fresh visit: the first
 * digit REPLACES what the field holds, the way a till's pad works — tap the
 * „1" in a quantity, press 3, it says 3, not 13. Returns null when the key
 * changed nothing on a fresh visit (a point where none is allowed).
 *
 * `number`: a type=number field — leading zeros go („05" → „5"). A PIN or a
 * phone is text and keeps them.
 */
export function nextDraft(
  draft: string | null,
  current: string,
  key: NumKey,
  o: { dot: boolean; number: boolean; maxLength?: number },
): string | null {
  if (key === 'clear') return ''
  if (key === 'back') return (draft ?? current).slice(0, -1)
  const base = draft ?? ''
  if (key === '.') {
    if (!o.dot || base.includes('.')) return draft
    return (base === '' ? '0' : base) + '.'
  }
  let next = base + key
  if (o.number) next = next.replace(/^0+(?=\d)/, '')
  if (o.maxLength && next.length > o.maxLength) return draft ?? null
  return next
}

/**
 * What a number field gets while the cashier is still typing, or null to hold
 * it back until Enter. An empty, zero or unfinished („12.") number is held:
 * the cart's kg field takes 0 as „remove the line", and „0.5" passes through
 * „0" on its way. A text field (PIN, phone) gets every key — „0" is a digit there.
 */
export function liveValue(draft: string, number: boolean): string | null {
  if (!number) return draft
  if (draft === '' || draft.endsWith('.')) return null
  return Number.parseFloat(draft) > 0 ? draft : null
}

/** What Enter writes: whatever is left, without a dangling point. */
export function enterValue(draft: string, number: boolean): string {
  return number && draft.endsWith('.') ? draft.slice(0, -1) : draft
}

/** What the pad's display shows. A PIN is dots — the till's screen faces the customer. */
export function shownValue(value: string, masked: boolean): string {
  return masked ? '•'.repeat(value.length) : value
}

/**
 * The pad's caption: the field's own words — its aria-label, else its <label>
 * (without the „required" star), else the placeholder, unless that is only a
 * mask („••••") or a sample value.
 */
export function padLabel(f: { aria: string; label: string; placeholder: string }): string {
  const clean = (s: string) => s.replace(/\s*\*\s*$/, '').trim()
  if (f.aria.trim()) return clean(f.aria)
  if (f.label.trim()) return clean(f.label)
  const ph = f.placeholder.trim()
  return /[\p{L}]/u.test(ph) ? ph : ''
}

// ── Letters ──────────────────────────────────────────────────────────────────

/** Insert at the caret (replacing a selection), or backspace. Null = nothing changes. */
export function textEdit(
  value: string,
  start: number,
  end: number,
  edit: { insert: string } | 'back',
  maxLength?: number,
): { value: string; caret: number } | null {
  if (edit === 'back') {
    if (start !== end) return { value: value.slice(0, start) + value.slice(end), caret: start }
    if (start === 0) return null
    return { value: value.slice(0, start - 1) + value.slice(start), caret: start - 1 }
  }
  const next = value.slice(0, start) + edit.insert + value.slice(end)
  if (maxLength && next.length > maxLength) return null
  return { value: next, caret: start + edit.insert.length }
}

export const DIGIT_ROW = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0']

/** The standard Georgian keyboard: each letter under its Latin key (ქ = Q, წ = W …). */
export const KA_ROWS = [
  ['ქ', 'წ', 'ე', 'რ', 'ტ', 'ყ', 'უ', 'ი', 'ო', 'პ'],
  ['ა', 'ს', 'დ', 'ფ', 'გ', 'ჰ', 'ჯ', 'კ', 'ლ'],
  ['ზ', 'ხ', 'ც', 'ვ', 'ბ', 'ნ', 'მ'],
]

/** Shift: the seven letters that share a key — W ჭ, R ღ, T თ, S შ, J ჟ, Z ძ, C ჩ. */
export const KA_SHIFT: Record<string, string> = {
  'წ': 'ჭ', 'რ': 'ღ', 'ტ': 'თ', 'ს': 'შ', 'ჯ': 'ჟ', 'ზ': 'ძ', 'ც': 'ჩ',
}

export const EN_ROWS = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
]

export function glyph(layout: 'ka' | 'en', base: string, shift: boolean): string {
  if (!shift) return base
  return layout === 'ka' ? (KA_SHIFT[base] ?? base) : base.toUpperCase()
}

// ── Where the number pad sits ────────────────────────────────────────────────

export type Rect = { left: number; top: number; right: number; bottom: number }

const overlaps = (a: Rect, b: Rect) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom

/**
 * The pad lives in a bottom corner — the side away from the field, so it never
 * covers what is being typed into; beside a dialog when the screen leaves room
 * (`avoid` = the dialog). When neither corner is clear (a narrow screen, a
 * phone) it takes the bottom and `reserve` asks the dialogs to move up above it.
 */
export function placeNumpad(o: {
  vw: number; vh: number; padW: number; padH: number; margin: number; field: Rect; avoid?: Rect
}): { side: 'left' | 'right'; reserve: boolean } {
  const avoid = o.avoid ?? o.field
  const preferred: 'left' | 'right' = (o.field.left + o.field.right) / 2 < o.vw / 2 ? 'right' : 'left'
  if (o.vw < 768) return { side: preferred, reserve: true }
  const top = o.vh - o.margin - o.padH
  const bottom = o.vh - o.margin
  const at = (side: 'left' | 'right'): Rect => side === 'left'
    ? { left: o.margin, right: o.margin + o.padW, top, bottom }
    : { left: o.vw - o.margin - o.padW, right: o.vw - o.margin, top, bottom }
  for (const side of [preferred, preferred === 'left' ? 'right' : 'left'] as const) {
    if (!overlaps(at(side), avoid)) return { side, reserve: false }
  }
  return { side: preferred, reserve: true }
}
